import React, { useEffect, useRef, useState } from 'react';

import { Honeybadger } from '@honeybadger-io/react';
import cs from 'classnames';
import { useSetRecoilState } from 'recoil';

import { socket, socketEmit, socketOff, socketOn } from 'lib/clients/socket';

import Button from 'components/shared/Button/Button';
import { registerStopTranscribing } from 'components/shared/Transcribe/lib/transcribeControl';
import state from 'components/state';

let mediaRecorder, silenceTimeout, pendingCleanupTimeout;

const Transcribe = ({
  id,
  name,
  value,
  setValue,
  setDisable,
  disabled,
  iconSize,
  withLabel = true
}) => {
  const setSocketHasConnection = useSetRecoilState(state.socketHasConnection);
  const [recording, setRecording] = useState(() => mediaRecorder?.state === 'recording');
  const [, setTranscribing] = useState(false);
  const text = useRef('');
  const textFutures = useRef({ solidText: '', softText: '' });
  const hasConnectedRef = useRef(false);
  const setValueRef = useRef(setValue);
  const stopRecordingRef = useRef(null);

  const startRecording = (e) => {
    e?.preventDefault();
    setRecording(true);
    setDisable(true);
  };

  useEffect(() => {
    setValueRef.current = setValue;
  }, [setValue]);

  useEffect(() => {
    registerStopTranscribing(() => stopRecordingRef.current?.());

    if (recording) {
      if (mediaRecorder?.state === 'recording') {
        // Remounted while recording active - cancel deferred cleanup, re-attach listeners
        clearTimeout(pendingCleanupTimeout);
        attachSocketListeners();
        setDisable(true);
      } else {
        deepGramAudio2text();
      }
    }
    if (!recording) {
      setTranscribing(false);
      clearTimeout(silenceTimeout);
      socketOff('transcript');
      socketOff('deepgram-disconnect');
    }
  }, [recording]);

  useEffect(() => {
    text.current = value;
    textFutures.current.solidText = value || '';
    textFutures.current.softText = '';
  }, []);

  useEffect(() => {
    setSocketHasConnection(!!socket?.connected);
    if (!socket?.connected) setTranscribing(false);
  }, [socket?.connected]);

  useEffect(() => {
    text.current = value;
    if (!recording) {
      textFutures.current.solidText = value || '';
      textFutures.current.softText = '';
    }
  }, [value]);

  const stopRecording = async (eventOrDontSendStop) => {
    if (typeof eventOrDontSendStop?.preventDefault === 'function') {
      eventOrDontSendStop.preventDefault();
    }
    const dontSendStop = eventOrDontSendStop === true;
    setTranscribing(false);
    clearTimeout(silenceTimeout);
    if (mediaRecorder && mediaRecorder?.state !== 'inactive') {
      stopMediaRecorder();
    }

    if (socket && socket.connected && !dontSendStop) {
      stopDeepgramListening();
    }
    setRecording(false);
    setDisable(false);
  };

  useEffect(() => {
    stopRecordingRef.current = stopRecording;
  });

  useEffect(() => {
    return () => {
      registerStopTranscribing(null);
      clearTimeout(silenceTimeout);
      socketOff('transcript');
      socketOff('deepgram-disconnect');
      if (mediaRecorder?.state === 'recording') {
        pendingCleanupTimeout = setTimeout(() => {
          if (mediaRecorder?.state === 'recording') {
            mediaRecorder?.stop();
            mediaRecorder?.stream.getTracks().forEach((track) => track.stop());
            mediaRecorder = null;
          }
          if (socket && socket.connected) {
            socketEmit('deepgram-stop');
          }
        }, 1000);
        return;
      }
      stopMediaRecorder();
      if (hasConnectedRef.current) {
        stopDeepgramListening();
      }
      setDisable(false);
    };
  }, []);

  const stopMediaRecorder = () => {
    mediaRecorder?.stop();
    mediaRecorder?.stream.getTracks().forEach((track) => track.stop());
    mediaRecorder = null;
    setRecording(false);
  };

  const stopDeepgramListening = () => {
    if (socket && socket.connected) {
      socketEmit('deepgram-stop');
      hasConnectedRef.current = false;
    }
    setTranscribing(false);
  };

  const notSupported = () => {
    alert('Browser not supported');
    setRecording(false);
  };

  const attachSocketListeners = () => {
    socketOn('deepgram-disconnect', () => stopRecordingRef.current?.(true));
    socketOn('transcript', async (m) => {
      if (m) {
        textFutures.current.solidText += ` ${m}`;
        textFutures.current.softText = '';
        const currentText = `${textFutures.current.solidText} ${textFutures.current.softText}`;
        setValueRef.current(currentText);

        clearTimeout(silenceTimeout);
        silenceTimeout = setTimeout(() => {
          stopRecordingRef.current?.();
        }, 15000);
      }
    });
  };

  const deepGramAudio2text = () => {
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        if (!MediaRecorder.isTypeSupported('audio/webm')) {
          const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

          if (isSafari) {
            mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/mp4' });
          } else {
            return notSupported();
          }
        } else {
          const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
            ? 'audio/webm;codecs=opus'
            : 'audio/webm';
          mediaRecorder = new MediaRecorder(stream, { mimeType });
        }

        socketEmit('deepgram-connect', {});
        hasConnectedRef.current = true;
        mediaRecorder?.addEventListener('dataavailable', async (event) => {
          if (event.data.size > 0) {
            setTranscribing(true);
            socketEmit('deepgram-packet-sent', event.data);
          }
        });

        mediaRecorder?.start(1100);
        attachSocketListeners();
      })
      .catch((error) => {
        Honeybadger.notify(
          `There's been an error trying to record. Please try again later. Make sure to use Chrome for the best experience. ${error}`
        );
      });
  };

  return (
    <div className="flex items-center gap-1">
      <Button
        className={cs(
          'Microphone flex !h-6 !w-6 items-center justify-center rounded-[3px] bg-transparent p-0 hover:!bg-neutral-100',
          recording && 'Microphone--recording !bg-primary-700',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer'
        )}
        id={id}
        name={name}
        type="button"
        variant="link"
        iconSize={iconSize}
        icon="microphone-2-mono"
        iconColor={recording ? 'white' : 'neutral'}
        isDisabled={disabled}
        onClick={recording ? stopRecording : startRecording}
      />
      {recording && withLabel && (
        <label className="m-0 text-xs italic text-neutral-500">transcribing</label>
      )}
    </div>
  );
};

export default Transcribe;
